import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { StoreApi } from 'zustand';

import { sendNewMessageV2 } from '@/services/sendNewMessageV2';
import {
  ADD_REFERENCES_SESSION_STATUS,
  type AddReferencesStore,
  deleteAddReferencesStore,
  getAddReferencesStore,
} from '@/services/stores/addReferencesStore';
import { createReferenceMessageData } from '@/types/messageData';
import { Sentry } from '@/util/sentry';

export type UseAddReferencesParams = {
  conversationId: string;
};

export type UseAddReferencesReturn = {
  store: StoreApi<AddReferencesStore>;
  /** Call when the user submits URLs from the action sheet. Posts each
   * formatted reference as a `reference` message and clears the session.
   * Returns `true` when the full flow completed — callers use this to trigger
   * celebration UX. */
  submitUrls: (
    formatBatchFn: (urls: string[]) => Promise<
      {
        external_reference_id: string;
        external_reference_type: 'pubmed' | 'pmc' | 'clinicaltrials' | 'cochrane' | 'other';
        title: string;
        authors: string;
        journal: string;
        year: number | null;
        url: string;
        abstract_snippet: string;
      }[]
    >
  ) => Promise<boolean>;
};

/**
 * Per-conversation lifecycle manager for the store. Creates on first call,
 * cleans up on conversation change or unmount.
 */
function useStoreLifecycle(conversationId: string): StoreApi<AddReferencesStore> {
  const prevConversationIdRef = useRef(conversationId);

  const store = useMemo(() => getAddReferencesStore(conversationId), [conversationId]);

  useEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      deleteAddReferencesStore(prevConversationIdRef.current);

      prevConversationIdRef.current = conversationId;
    }
  }, [conversationId]);

  useEffect(() => {
    return () => {
      deleteAddReferencesStore(conversationId);
    };
  }, [conversationId]);

  return store;
}

/**
 * Hook for the per-conversation "Add references" URL-entry session. Opens a
 * proactive action sheet, formats the pasted URLs, and posts each as a
 * standalone `reference` message. It writes no bubble resolution — this is a
 * bubble-independent entry point.
 */
export function useAddReferences({
  conversationId,
}: UseAddReferencesParams): UseAddReferencesReturn {
  const store = useStoreLifecycle(conversationId);

  const submitUrls = useCallback<UseAddReferencesReturn['submitUrls']>(
    async (formatBatchFn) => {
      const { urls, status } = store.getState();

      // Synchronous in-flight gate. The sheet's Send button is disabled while
      // formatting, but that disabled state lands on the next render — taps
      // landing before it each fired a full submit and posted duplicate
      // reference messages. Re-checking the store here closes that race.
      if (status === ADD_REFERENCES_SESSION_STATUS.FORMATTING) {
        return false;
      }

      // Dedupe identical rows so a URL pasted twice is only formatted and
      // posted once.
      const sanitized = [...new Set(urls.map((url) => url.trim()).filter((url) => url.length > 0))];

      if (sanitized.length === 0) {
        return false;
      }

      store.getState().markFormatting();

      // References already posted this session (a prior partial-failure).
      // Retrying re-formats the same URLs, so skip anything that landed to
      // avoid re-posting it.
      const seenKeys = new Set(store.getState().sentReferenceKeys);

      const newlySentKeys: string[] = [];

      try {
        const references = await formatBatchFn(sanitized);

        for (const reference of references) {
          const key = `${reference.external_reference_type}:${reference.external_reference_id || reference.url}`;

          if (seenKeys.has(key)) {
            continue;
          }

          await sendNewMessageV2({
            conversationId,
            message: `Reference: ${reference.title}${reference.url ? `\n\nLink: ${reference.url}` : ''}`,
            data: createReferenceMessageData(reference),
          });

          seenKeys.add(key);

          newlySentKeys.push(key);
        }

        store.getState().reset();

        return true;
      } catch (error) {
        if (newlySentKeys.length > 0) {
          store.getState().markReferencesSent(newlySentKeys);
        }

        const message =
          error instanceof Error ? error.message : 'Unable to send references. Please try again.';

        store.getState().setError(message);

        Sentry.captureException(error, {
          tags: { component: 'useAddReferences', source: 'submit-urls' },
          extra: { conversationId, urlCount: sanitized.length },
        });

        return false;
      }
    },
    [conversationId, store]
  );

  return { store, submitUrls };
}
